import * as React from 'react';
import styled from 'styled-components';

import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';
import { Gradient } from '@longline/aqua-ui/controls/Gradient';

import { ILayer } from '../types/ILayer';
import { LayerUtils } from '../util/LayerUtils';

interface IProps {
  /** @ignore */
  className?: string;
  /** Layer to render legend for. */
  layer: ILayer;
}

/**
 * Hook to get responsive tick count based on window width.
 * Returns fewer ticks on narrow screens.
 */
const useTickCount = (): number => {
  const getTickCount = () => {
    const width = window.innerWidth;
    if (width < 768) return 2;  // Very narrow: 3 ticks
    if (width < 1024) return 3; // Tablet: 4 ticks
    return 5;                   // Desktop: 6 ticks
  };

  const [tickCount, setTickCount] = React.useState(getTickCount);

  React.useEffect(() => {
    const handleResize = () => setTickCount(getTickCount());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return tickCount;
};

/**
 * Returns "nice" ticks between min and max.
 * 
 * @param min original min
 * @param max original max
 * @param count desired number of segments (5 ticks = 6 values)
 */
const niceTicks = (min: number, max: number, count = 5): number[] => {
  if (min === max) return [min]; // degenerate case

  const span = max - min;
  const rawStep = span / count;

  // 1. Find magnitude of step
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / magnitude; // normalized to [1, 10)

  // 2. Snap normalized step to 1, 2, 5, 10
  let niceNorm: number;
  if (norm < 1.5) niceNorm = 1;
  else if (norm < 3) niceNorm = 2;
  else if (norm < 7) niceNorm = 5;
  else niceNorm = 10;

  const step = niceNorm * magnitude;

  // 3. Compute nice min and nice max
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;

  // 4. Build ticks
  const ticks = [];
  for (let v = niceMin; v <= niceMax + step * 0.5; v += step) {
    ticks.push(Number(v.toFixed(12))); // cleanup floating point noise
  }

  return ticks;
}

const LegendBase = (props: IProps) => {
  const def = LayerUtils.layerTypeToDefinition(props.layer.type);
  const [min, max] = LayerUtils.getMinMax(props.layer);
  const tickCount = useTickCount();
  const ticks = niceTicks(min, max, tickCount);
  const step = ticks.length > 1 ? ticks[1] - ticks[0] : 1;
  const decimals = step >= 1 ? 0 : Math.min(6, Math.max(0, -Math.floor(Math.log10(step))));
  return (
    <div className={props.className}>
      <Quantity><>{def.name} {def.unit ? <>({def.unit})</> : null}</></Quantity>
      <Min><NumberFormatter value={min} decimals={decimals}/></Min>
      <GradientHolder>
        <Gradient gradientStops={props.layer.gradient} rounded thickness={8}/>
        {ticks
          .map(t => ({ tick: t, ratio: (t - min) / (max - min) }))
          .filter(({ ratio }) => ratio > 0.08 && ratio < 0.92)
          .map(({ tick, ratio }, i) => (
            <Tick key={i} x={`${ratio * 100}%`}>
              <NumberFormatter value={tick} decimals={decimals}/>
            </Tick>
          ))}
      </GradientHolder>
      <Max><NumberFormatter value={max} decimals={decimals}/></Max>
    </div>
  )
}

const Quantity = styled.div`
  white-space: nowrap;
  width: 120px;
`

const Min = styled.div`
  width: 60px;
  text-align: right;
`

const Max = styled.div`
  width: 60px;
`

const GradientHolder = styled.div`
  position: relative;
  flex: 1;
  border-radius: 4px;
  box-shadow: 0px 0px 4px 1px rgba(0,0,0,0.5);
`

const Tick = styled.div<{ x: string }>`
  position: absolute;
  left: ${p => p.x};
  top: 50%;
  height: 100%;
  translate: -50% -50%;
  font-size: 6px;
  line-height: 100%;
  display: flex;
  align-items: center;
  &:before {
    position: absolute;
    left: 50%;
    top: 100%;
    bottom: -1px;
    content: '';
    z-index: -1;
    border-left: solid 1px #fff;
  }
  &:after {
    position: absolute;
    left: 50%;
    top: -1px;
    bottom: 100%;
    content: '';
    z-index: -1;
    border-left: solid 1px #fff;
  }

`

const LegendStyled = styled(LegendBase)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 80%;
`

const Legend = (props: IProps) => <LegendStyled {...props}/>

export { Legend }
